import type { VerdictCounts } from '@/lib/reference/fleet'

/**
 * One subject's capability, as a table cell.
 *
 * Shows the effective verdict — baseline with any exception already applied —
 * because that is what a planner acts on. Where an exception did the
 * deciding, the badge says so: an override that looks like the standing rule
 * is one nobody remembers to take back off.
 */
export function CapabilitySummary({
  counts,
  onEdit,
}: {
  counts: VerdictCounts
  /** Opens the exception dialog. Omitted for a reader who cannot edit. */
  onEdit?: () => void
}) {
  // Same glyphs as the matrix, so the cell needs no key. A group at zero is
  // dropped rather than shown as "0": the column is narrow and a zero says
  // nothing a missing glyph does not.
  const body = (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {counts.yes > 0 && (
        <span className="t-number-sm text-semantic-up">✓{counts.yes}</span>
      )}
      {counts.no > 0 && (
        <span className="t-number-sm text-semantic-down">✕{counts.no}</span>
      )}
      {counts.unset > 0 && (
        <span className="t-number-sm text-muted-soft">—{counts.unset}</span>
      )}
      {counts.exceptions > 0 && (
        <span className="t-caption-strong rounded-[100px] bg-ink px-2 py-0.5 text-on-dark">
          Exception
        </span>
      )}
    </span>
  )

  const title = `${counts.yes} allowed · ${counts.no} denied · ${counts.unset} not set · ${counts.tails} tails`

  if (!onEdit) {
    return (
      <span className="inline-block" title={title}>
        {body}
      </span>
    )
  }

  return (
    <button
      type="button"
      title={title}
      onClick={(event) => {
        event.stopPropagation()
        onEdit()
      }}
      className="rounded-[8px] px-1.5 py-1 transition-colors hover:bg-surface-strong"
    >
      {body}
    </button>
  )
}
